import { yupResolver } from "@hookform/resolvers/yup";
import Settings from "@mui/icons-material/Settings";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { useSupabaseCallback } from "../../../server/supabase/hooks";
import { useDialog } from "../../../utils/hooks/use-dialog";
import { fromISO8601_DATE, toISO8601_DATE } from "../../../utils/iso8601";
import yup, { yupISO8601Date } from "../../../utils/yup";
import { SupabaseHRISService } from "../../integrations/server/supabase-hris-service";

import type { HrisConfig } from "../../integrations/server/supabase-hris-service";
import type { FC } from "react";

const schema = yup.object({
  inactive_people_cut_off_date: yupISO8601Date.nullable().default(null),
});

export const HrisIntegrationConfigureButton: FC<{
  integration: HrisConfig;
  reloadIntegration: () => void;
}> = ({ integration, reloadIntegration }) => {
  const { handleClose, ...dialog } = useDialog();

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: integration,
  });

  const { id } = integration;

  const handleSubmit = useSupabaseCallback(
    async ({ supabase }, updates: yup.InferType<typeof schema>) => {
      const { error } = await new SupabaseHRISService(supabase).update(id, updates);

      if (error) {
        toast.error("There was an error updating the HRIS configuration");
        return;
      }

      toast.success("HRIS configuration updated");

      handleClose();
      reloadIntegration();
    },
    [reloadIntegration, handleClose, id],
  );

  return (
    <>
      <Button
        data-analytics-id="hris-integration-configure"
        onClick={dialog.handleOpen}
        variant="outlined"
        color="secondary"
        startIcon={<Settings />}
      >
        Configure
      </Button>
      <Dialog open={dialog.open} onClose={handleClose} fullWidth maxWidth="sm">
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(handleSubmit)}>
            <DialogTitle>Configure HRIS Integration</DialogTitle>
            <DialogContent>
              <Stack spacing={2} mt={2}>
                <Controller
                  name="inactive_people_cut_off_date"
                  control={methods.control}
                  render={({ field, fieldState }) => (
                    <DatePicker
                      {...field}
                      value={field.value ? fromISO8601_DATE(field.value) : null}
                      onChange={(value) => field.onChange(value ? toISO8601_DATE(value) : null)}
                      label="Inactive People Cut Off Date"
                      slotProps={{
                        textField: {
                          id: "inactive-people-cut-off-date",
                          error: Boolean(fieldState.error),
                          helperText:
                            fieldState.error?.message ??
                            "We won’t attempt to sync inactive people with a termination date before this date.",
                          sx: { mr: 2 },
                        },
                        field: {
                          clearable: true,
                          onClear: () => field.onChange(null),
                        },
                      }}
                    />
                  )}
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button
                data-analytics-id="hris-integration-configure-dialog-cancel"
                onClick={handleClose}
                color="warning"
              >
                Cancel
              </Button>
              <Button
                data-analytics-id="hris-integration-configure-dialog-save"
                type="submit"
                color="primary"
              >
                Save
              </Button>
            </DialogActions>
          </form>
        </FormProvider>
      </Dialog>
    </>
  );
};
